import { useEffect, useRef, useState } from 'react';
import { X, Upload, Loader2, Trash2 } from 'lucide-react';
import * as tus from 'tus-js-client';
import api from '../../api/client';
import { Modal } from '../common/Modal';

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  tusUpload?: tus.Upload;
}

interface UploadModalProps {
  folderId: number | null;
  projectId: number | null;
  initialFiles?: File[];
  onClose: () => void;
  onUploaded: () => void;
}

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function UploadModal({ folderId, projectId, initialFiles = [], onClose, onUploaded }: UploadModalProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialProcessedRef = useRef(false);

  useEffect(() => {
    if (initialFiles.length > 0 && !initialProcessedRef.current) {
      initialProcessedRef.current = true;
      const newFiles: UploadFile[] = initialFiles.map((f) => ({
        file: f,
        id: generateUUID(),
        progress: 0,
        status: 'pending',
      }));
      setFiles((prev) => [...prev, ...newFiles]);
    }
  }, []);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const newFiles: UploadFile[] = Array.from(fileList).map((f) => ({
      file: f,
      id: generateUUID(),
      progress: 0,
      status: 'pending',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const uploadSingle = async (item: UploadFile) => {
    const token = localStorage.getItem('token');
    const baseUrl = api.defaults.baseURL || import.meta.env.VITE_API_URL || '';

    updateFile(item.id, { status: 'uploading', progress: 0 });

    try {
      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(item.file, {
          endpoint: `${baseUrl.replace(/\/$/, '')}/api/v1/tus`,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          chunkSize: Number(import.meta.env.VITE_TUS_CHUNK_SIZE) || 2 * 1024 * 1024,
          metadata: {
            filename: item.file.name,
            filetype: item.file.type || 'application/octet-stream',
            project_id: projectId ? String(projectId) : '',
            folder_id: folderId ? String(folderId) : '',
          },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          onError: (error) => {
            const message = error.message || 'Yükleme başarısız';
            updateFile(item.id, { status: 'error', error: message });
            reject(error);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percentage = bytesTotal > 0 ? Math.round((bytesUploaded / bytesTotal) * 100) : 0;
            updateFile(item.id, { progress: percentage });
          },
          onSuccess: () => {
            updateFile(item.id, { status: 'done', progress: 100 });
            resolve();
          },
        });

        updateFile(item.id, { tusUpload: upload });
        upload.start();
      });
    } catch (err) {
      const isAborted = (err as Error).message?.toLowerCase().includes('abort');
      if (isAborted) {
        updateFile(item.id, { status: 'error', error: 'İptal edildi' });
      }
      throw err;
    }
  };

  const startUpload = async () => {
    const pending = files.filter((f) => f.status === 'pending');
    if (pending.length === 0) return;
    setIsUploading(true);

    // Paralel upload max 3
    const queue = [...pending];
    const running = new Set<Promise<void>>();

    const processNext = async () => {
      const item = queue.shift();
      if (!item) return;
      const p = uploadSingle(item).finally(() => {
        running.delete(p);
        return processNext();
      });
      running.add(p);
    };

    // Başlat max 3
    for (let i = 0; i < Math.min(3, queue.length); i++) {
      processNext();
    }

    const results = await Promise.allSettled(Array.from(running));
    setIsUploading(false);
    const hasError = results.some((r) => r.status === 'rejected');
    if (!hasError) {
      onUploaded();
    }
  };

  const cancelUpload = (id: string) => {
    const file = files.find((f) => f.id === id);
    if (file?.tusUpload) {
      file.tusUpload.abort();
    }
    updateFile(id, { status: 'error', error: 'İptal edildi' });
  };

  const removeFile = (id: string) => {
    cancelUpload(id);
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <Modal
      open
      onClose={onClose}
      align="sheet"
      overlayClassName="bg-black/50"
      panelUnstyled
      panelClassName="w-full max-w-none rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-lg sm:rounded-xl sm:p-6"
      labelledBy="upload-modal-title"
      data-testid="upload-modal"
    >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="upload-modal-title" className="text-lg font-bold text-slate-900">Dosya Yükle</h2>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Drag & Drop */}
        <div
          data-testid="upload-drop-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFiles(e.dataTransfer.files);
          }}
          className="mb-4 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center"
        >
          <Upload className="mx-auto mb-2 h-8 w-8 text-slate-400" />
          <p className="text-sm text-slate-600">Dosyaları buraya sürükleyin</p>
          <p className="text-xs text-slate-400">veya</p>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="mt-2 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Dosya Seç
          </button>
          <input ref={inputRef} data-testid="upload-file-input" type="file" accept="*/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        </div>

        {/* Liste */}
        {files.length > 0 && (
          <div className="mb-4 max-h-[40vh] space-y-2 overflow-y-auto sm:max-h-60">
            {files.map((f) => (
              <div key={f.id} data-testid={`upload-file-row-${f.file.name}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-1 flex items-center justify-between">
                  <span className="truncate text-sm font-medium text-slate-800">{f.file.name}</span>
                  <div className="flex items-center gap-1">
                    {f.status === 'uploading' && (
                      <button
                        onClick={() => cancelUpload(f.id)}
                        className="rounded p-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        İptal
                      </button>
                    )}
                    <button
                      onClick={() => removeFile(f.id)}
                      data-testid={`remove-file-${f.file.name}`}
                      className="rounded p-1 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                  <div
                    className={`h-full rounded-full transition-all ${
                      f.status === 'error' ? 'bg-red-500' : f.status === 'done' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${f.progress}%` }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                  <span data-testid={`upload-status-${f.file.name}`}>
                    {f.status === 'done'
                      ? 'Tamamlandı'
                      : f.status === 'error'
                      ? f.error || 'Hata'
                      : f.status === 'uploading'
                      ? 'Yükleniyor...'
                      : 'Bekliyor'}
                  </span>
                  <span>{f.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Kapat
          </button>
          <button
            onClick={startUpload}
            disabled={isUploading || files.filter((f) => f.status === 'pending').length === 0}
            data-testid="start-upload-button"
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
          >
            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
            Yükle
          </button>
        </div>
    </Modal>
  );
}
